import { Injectable } from '@angular/core';
import { AdditionalLocationListResponse, CompanyDetailsSection, GridData, LocationList, RegistrationState, SubmitBusinessPayload, SubmitRegistrationResponse } from './definitions/registration.interface';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { CountryCode, Icons, RegistrationRequestType, countries, provinces, states, ErrorMessage } from '../../services/shared/shared-enums';
import { SharedService } from '../../services/shared/shared.service';
import { AnalyticsDataLayer } from '../../services/analytics/definitions';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { SharedUtilService } from '../../services/shared/shared-util.service';
import { Address } from '../../services/shared/shared-interface';
import { HttpErrorResponse } from '@angular/common/http';
import { KngButtonState } from 'kinetic-angular';
import { KngUploadFileState } from 'projects/kinetic-angular/src/lib/components/upload-file';
import { RegistrationFactory } from './registration.factory';

@Injectable({
	providedIn: 'root'
})
export class RegistrationUtilService
{
	constructor(private readonly localStorageService: LocalStorageService, private readonly sharedService: SharedService, private readonly analyticsService: AnalyticsService, private readonly sharedUtilService: SharedUtilService)
	{}

	getSelectedLocations(locations: LocationList[], selectedLocations: string[])
	{
		return locations.filter(location=>selectedLocations.includes(location.locationGRID));
	}
	getLocationsByGrids(locations: GridData[]) : string[]
	{
		return locations.map(location=> location.locationGRID);
	}
	isBusinessAccountRegistration(response: SubmitRegistrationResponse)
	{ 
		if(response.requestType === RegistrationRequestType.businessAdminUser)
		{
			this.localStorageService.setItems('new_business_account', 'true');
		}
	}
	isPrimaryLocationAddressPresent(response: AdditionalLocationListResponse)
	{
		return !!(response.primaryLocationInfo.addressLine1 && response.primaryLocationInfo.city && response.primaryLocationInfo.state && response.primaryLocationInfo.zipCode && response.primaryLocationInfo.country);
	}
	displayCountry(country: string)
	{
		if(country)
		{
			const countryToDisplay = countries.filter(ele => ele.value === country);
			return countryToDisplay[0].label;
		}
		return '';
	}
	displayState(state: string, country: string) : string
	{
		const countryCode = this.sharedService.checkCountryCode(country);
		if(state && this.isValidCountry(countryCode))
		{
			const targetArray = countryCode === CountryCode.unitedStatesofAmerica? states : provinces;
			const ele = targetArray.find(ele=>ele.value === state.toLocaleUpperCase());
			return ele? ele.label : '';
		}
		return '';
	}
	isValidCountry(countryCode: string)
	{
		return countryCode === CountryCode.unitedStatesofAmerica || countryCode === CountryCode.canadaCountry;
	}
	isPrimaryLocationAvailable(response: AdditionalLocationListResponse)
	{
		return !!(response.primaryLocationInfo.companyName && this.isPrimaryLocationAddressPresent(response));
	}
	showContactDealer(element: HTMLDivElement, email: string) 
	{
		element.classList.remove('hidden');
		element.focus();
		const analyticsData: AnalyticsDataLayer = {
			event: 'error',
			link: {
				name: 'error'
			},
			error: {
				message: ErrorMessage.companyNotFoundError
			}
		};
		this.analyticsService.trackRegistrationByEvent(analyticsData, email);
	}
	getStateProvinceValues(country: string)
	{
		return this.sharedService.getStateProvinceValues(country);
	}
	mapAdditionalLocationList(additionalLocationList: AdditionalLocationListResponse, response: AdditionalLocationListResponse)
	{
		additionalLocationList.primaryLocationInfo.primaryLocationGRID = this.sharedService.emptyStringHandler(response?.primaryLocationInfo?.primaryLocationGRID);
		additionalLocationList.primaryLocationInfo.adminLocationID = this.sharedService.emptyStringHandler(response?.primaryLocationInfo?.adminLocationID);
		additionalLocationList.primaryLocationInfo.topLevelGrid = this.sharedService.emptyStringHandler(response?.primaryLocationInfo?.topLevelGrid);
		additionalLocationList.primaryLocationInfo.companyName = this.sharedService.emptyStringHandler(response?.primaryLocationInfo?.companyName);
		additionalLocationList.primaryLocationInfo.addressLine1 = this.sharedService.emptyStringHandler(response?.primaryLocationInfo?.addressLine1);
		additionalLocationList.primaryLocationInfo.addressLine2 = this.sharedService.emptyStringHandler(response?.primaryLocationInfo?.addressLine2);
		additionalLocationList.primaryLocationInfo.city = this.sharedService.emptyStringHandler(response?.primaryLocationInfo?.city);
		additionalLocationList.primaryLocationInfo.state = this.sharedService.emptyStringHandler(this.displayState(response?.primaryLocationInfo?.state, response?.primaryLocationInfo?.country));
		additionalLocationList.primaryLocationInfo.zipCode = this.sharedService.emptyStringHandler(this.sharedUtilService.formatZipCode(response?.primaryLocationInfo?.zipCode, this.sharedService.checkCountryCode(response?.primaryLocationInfo?.country)));
		additionalLocationList.primaryLocationInfo.country = this.sharedService.emptyStringHandler(this.displayCountry(this.sharedService.checkCountryCode(response?.primaryLocationInfo?.country)));
		return additionalLocationList;
	}
	formatLocationList(locationList: LocationList[])
	{
		const locations: GridData[] = [];
		locationList.forEach(location =>
		{
			if (location)
			{
				location.zipCode = location.zipCode?this.sharedUtilService.formatZipCode(location.zipCode, this.sharedService.checkCountryCode(location.country)):'';
				const locationDetails = {
					locationGRID: this.sharedService.nullValueHandler(location.locationGRID),
					companyName: this.sharedService.nullValueHandler(location.companyName),
					address: this.sharedService.nullValueHandler([ location.addressLine1, location.addressLine2, location.city, location.state, location.zipCode ].filter(Boolean).join(', ')),
					country: this.sharedService.nullValueHandler(this.displayCountry(this.sharedService.checkCountryCode(location.country))),
					checked: false
				};
				locations.push(locationDetails);
			}
		});
		return locations;
	}
	trackAnalyticsForRegistrationError(errorMessage: string, email: string)
	{
		this.analyticsService.trackRegistrationByEvent({
			event: 'error',
			link: {
				name: 'error'
			},
			error: {
				message: errorMessage
			}
		}, email);
	}
	trackAnalyticsEvent(data: AnalyticsDataLayer, email: string): void
	{
		this.analyticsService.trackRegistrationByEvent(data, email);
	}
	mapAddress(address: Address, companyDetails: CompanyDetailsSection): Address
	{
		address.addressLine1 = companyDetails.addressLine1;
		address.addressLine2 = companyDetails.addressLine2;
		address.country = companyDetails.country;
		address.city = companyDetails.city;
		address.state = companyDetails.state;
		address.zipCode = companyDetails.zipCode? this.sharedUtilService.formatZipCode(companyDetails.zipCode, companyDetails.country) : '';
		return address;	
	}
	getUploadFileLabel(res:RegistrationState, uploadFileState:KngUploadFileState)
	{
		uploadFileState.isLoading = false;
	    uploadFileState.dragDropSection = res.uploadFileSection.uploadFile.dragDropLabel;
		uploadFileState.or = res.uploadFileSection.uploadFile.orLabel;
		uploadFileState.successMessage = res.uploadFileSection.uploadFile.success;
		uploadFileState.uploadButtons = res.uploadFileSection.uploadFile.uploadButtons;
		uploadFileState.errorImage.altText = res.uploadFileSection.uploadFile.errorIcon.altText;
		uploadFileState.successImage.altText = res.uploadFileSection.uploadFile.successIcon.altText;
		uploadFileState.dragDropImage.altText = res.uploadFileSection.uploadFile.onLoadIcon.altText;
		uploadFileState.errorImage.iconPath = Icons.actionRed;
		uploadFileState.successImage.iconPath = Icons.checkGreen;
		uploadFileState.dragDropImage.iconPath = Icons.dragDropIcon;
	}
	async handleDownloadCommonError(error: HttpErrorResponse, state:RegistrationState, downloadButtonState: KngButtonState, email: string )
	{
		if (error) 
		{
			const message = JSON.parse(await error.error.text()).message;
			state.uploadFileSection.errorState= {
				messages: [{ message: message, cssClass: '' }],
				id: '',
				statusCode: 500,
				image: { iconPath: '', altText: '' }
			};
		    state.uploadFileSection.download.errorMessage=state.uploadFileSection.errorState.messages[0].message;
			downloadButtonState.disabled = false;
			this.analyticsService.trackRegistrationByEvent({
				event: 'Download Failed',
				link: {
					name: 'Download Template Button'
				},
				error: {
					message: state.uploadFileSection.errorState.messages[0].message
				}
			}, email);
		}
	}
	submitLocationPayload(isUploadSuccess:boolean, additionalLocations: LocationList[], selectedLocationGrids: string[] )
	{
		let submitPayload:SubmitBusinessPayload;
		if(isUploadSuccess)
		{
			submitPayload=RegistrationFactory.buildSubmitUploadFileLocationsPayload();
		}
		else
		{
			submitPayload=RegistrationFactory.buildSubmitLocationsPayload(this.getSelectedLocations(additionalLocations, selectedLocationGrids));
		}
		return submitPayload;
	}
}
